import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VOICE_NOTE_LABELS, VOICE_WAVE_BARS } from '../../constants/voice-note.constants';
import { flatWave, voiceBarHeight } from '../../services/voice-recorder';

let activeAudio: HTMLAudioElement | null = null;

export function stopVoiceNotePlayback(): void {
  activeAudio?.pause();
}

function claimPlayback(audio: HTMLAudioElement): void {
  if (activeAudio && activeAudio !== audio) activeAudio.pause();
  activeAudio = audio;
}

@Component({
  selector: 'app-voice-note-player',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="voice-note">
      <button type="button" class="voice-note-toggle" (click)="toggle($event)"
        [attr.aria-label]="playing ? labels.voicePauseAria : labels.voicePlayAria">
        <svg *ngIf="playing" viewBox="0 0 24 24" aria-hidden="true">
          <rect x="6" y="5" width="4" height="14" rx="1" />
          <rect x="14" y="5" width="4" height="14" rx="1" />
        </svg>
        <svg *ngIf="!playing" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 5.5v13l11-6.5-11-6.5z" />
        </svg>
      </button>
      <div class="voice-note-wave" role="slider" tabindex="0"
        [attr.aria-label]="labels.voiceSeekAria"
        [attr.aria-valuemin]="0"
        [attr.aria-valuemax]="duration"
        [attr.aria-valuenow]="shownSeconds"
        (click)="seek($event)">
        <span *ngFor="let bar of bars; let i = index" class="voice-note-bar"
          [class.is-played]="barPlayed(i)" [style.height.px]="barHeight(bar)"></span>
      </div>
      <span class="voice-note-clock">{{ clock }}</span>
    </div>
  `,
  styles: [`
    :host { display: block; min-width: 220px; }
    .voice-note { display: flex; align-items: center; gap: 10px; }
    .voice-note-toggle {
      width: 32px; height: 32px; flex-shrink: 0; border: none; border-radius: 50%;
      background: #1d4ed8; color: #fff; display: flex; align-items: center; justify-content: center;
      cursor: pointer; padding: 0;
    }
    .voice-note-toggle svg { width: 14px; height: 14px; fill: currentColor; }
    .voice-note-wave {
      display: flex; align-items: center; gap: 2px; height: 32px; flex: 1; min-width: 0; cursor: pointer;
    }
    .voice-note-bar {
      flex: 1 1 0; min-width: 2px; max-width: 4px; border-radius: 999px;
      background: rgba(29, 78, 216, 0.35);
    }
    .voice-note-bar.is-played { background: #1d4ed8; }
    .voice-note-clock {
      width: 36px; flex-shrink: 0; text-align: right; font-size: 12px; font-variant-numeric: tabular-nums;
      color: #1e3a8a;
    }
  `]
})
export class VoiceNotePlayerComponent implements OnChanges, OnDestroy {
  @Input() src = '';
  @Input() seconds = 0;
  @Input() waveform: number[] = [];

  readonly labels = VOICE_NOTE_LABELS;
  bars: number[] = flatWave();
  playing = false;
  private playbackSeconds = 0;
  private playbackRatio = 0;
  private decodedSeconds = 0;
  private audio?: HTMLAudioElement;
  private loadedFor: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.waveform?.length) {
      this.bars = this.waveform;
    } else if (changes['src'] && this.src && this.loadedFor !== this.src) {
      this.loadedFor = this.src;
      this.bars = flatWave();
      void this.loadWave(this.src);
    }
  }

  ngOnDestroy(): void {
    if (!this.audio) return;
    this.audio.onended = null;
    this.audio.ontimeupdate = null;
    this.audio.onpause = null;
    this.audio.pause();
    if (activeAudio === this.audio) activeAudio = null;
  }

  get duration(): number {
    return this.seconds || this.decodedSeconds || 0;
  }

  get shownSeconds(): number {
    if (this.playing || this.playbackSeconds > 0) return Math.floor(this.playbackSeconds);
    return Math.floor(this.duration);
  }

  get clock(): string {
    const total = this.playing || this.playbackSeconds > 0 ? this.playbackSeconds : this.duration;
    const safe = Math.max(0, Math.floor(total || 0));
    const minutes = Math.floor(safe / 60);
    const secs = safe % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  barHeight(level: number): number {
    return voiceBarHeight(level);
  }

  barPlayed(index: number): boolean {
    if (!this.playing && this.playbackRatio <= 0) return false;
    const count = this.bars.length || 1;
    return (index + 0.5) / count <= this.playbackRatio;
  }

  toggle(event: Event): void {
    event.stopPropagation();
    if (!this.src) return;
    if (this.audio) {
      if (this.audio.paused) {
        claimPlayback(this.audio);
        void this.audio.play();
      } else {
        this.audio.pause();
      }
      this.sync(this.audio);
      return;
    }
    this.play(0);
  }

  seek(event: MouseEvent): void {
    event.stopPropagation();
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    this.play(ratio);
  }

  private play(ratio: number): void {
    if (!this.src) return;
    let audio = this.audio;
    if (!audio) {
      audio = new Audio(this.src);
      this.audio = audio;
      audio.ontimeupdate = () => this.sync(audio!);
      audio.onpause = () => this.sync(audio!);
      audio.onended = () => {
        if (activeAudio === audio) activeAudio = null;
        this.playing = false;
        this.playbackRatio = 0;
        this.playbackSeconds = 0;
      };
    }
    claimPlayback(audio);
    const start = () => {
      const duration = audio!.duration;
      if (Number.isFinite(duration) && duration > 0) {
        audio!.currentTime = Math.min(duration, Math.max(0, ratio * duration));
        if (!this.seconds) this.decodedSeconds = Math.max(1, Math.round(duration));
      }
      void audio!.play();
      this.sync(audio!);
    };
    if (audio.readyState >= 1) start();
    else audio.addEventListener('loadedmetadata', start, { once: true });
  }

  private sync(audio: HTMLAudioElement): void {
    if (this.audio !== audio) return;
    const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
    this.playbackRatio = duration ? audio.currentTime / duration : 0;
    this.playbackSeconds = audio.currentTime || 0;
    this.playing = !audio.paused;
  }

  private async loadWave(src: string): Promise<void> {
    try {
      const response = await fetch(src);
      if (!response.ok || this.src !== src) return;
      const context = new AudioContext();
      const buffer = await context.decodeAudioData(await response.arrayBuffer());
      await context.close();
      if (this.src !== src || this.waveform?.length) return;
      const channel = buffer.getChannelData(0);
      const out: number[] = [];
      const size = Math.max(1, Math.floor(channel.length / VOICE_WAVE_BARS));
      for (let i = 0; i < VOICE_WAVE_BARS; i++) {
        const start = i * size;
        const end = Math.min(channel.length, start + size);
        let peak = 0;
        const step = Math.max(1, Math.floor((end - start) / 40));
        for (let j = start; j < end; j += step) peak = Math.max(peak, Math.abs(channel[j] ?? 0));
        out.push(peak);
      }
      const max = Math.max(...out, 0.001);
      this.bars = out.map((value) => Math.max(0.16, Math.min(1, value / max)));
      if (!this.seconds) this.decodedSeconds = Math.max(1, Math.round(buffer.duration));
    } catch {
      // Flat bars stay in place. Playback still uses the audio file.
    }
  }
}
